import React, { useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "../AuthContext";

const WARNING_MS = 5 * 60 * 1000; // show at 5 min remaining
const TICK_MS = 1000;

// Decode a JWT payload without verifying the signature. Returns null on failure.
function decodeJwt(token) {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = payload + "===".slice((payload.length + 3) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

const fmtMMSS = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const SessionWarning = () => {
  const { token, refreshSession, logout } = useContext(AuthContext);
  const [expMs, setExpMs] = useState(null);
  const [remaining, setRemaining] = useState(null);
  const [extending, setExtending] = useState(false);
  const timerRef = useRef(null);

  // Reset when the token changes (login / refresh / logout)
  useEffect(() => {
    if (!token) {
      setExpMs(null);
      setRemaining(null);
      return;
    }
    const decoded = decodeJwt(token);
    if (!decoded || !decoded.exp) {
      setExpMs(null);
      setRemaining(null);
      return;
    }
    setExpMs(decoded.exp * 1000);
  }, [token]);

  // Countdown tick
  useEffect(() => {
    if (!expMs) return undefined;
    const tick = () => setRemaining(expMs - Date.now());
    tick();
    timerRef.current = setInterval(tick, TICK_MS);
    return () => clearInterval(timerRef.current);
  }, [expMs]);

  const handleExtend = async () => {
    setExtending(true);
    const ok = await refreshSession();
    setExtending(false);
    if (!ok) {
      // If refresh fails (e.g. token already expired) fall back to a clean logout.
      logout();
    }
  };

  if (!token || remaining == null) return null;
  if (remaining <= 0) return null; // let axios interceptor handle real expiry
  if (remaining > WARNING_MS) return null;

  return (
    <div
      className="fixed bottom-5 right-5 z-50 bg-white border border-amber-300 shadow-lg rounded-lg px-4 py-3 flex items-center gap-4 max-w-sm"
      data-testid="session-warning"
      role="alert"
      aria-live="polite"
    >
      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-gray-900">Your session is about to expire</p>
        <p className="text-xs text-gray-600" data-testid="session-warning-countdown">
          Signing out in {fmtMMSS(remaining)}
        </p>
      </div>
      <button
        onClick={handleExtend}
        disabled={extending}
        data-testid="session-warning-extend"
        className="flex-shrink-0 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-3 py-1.5 rounded-md disabled:opacity-50"
      >
        {extending ? "Extending..." : "Extend session"}
      </button>
    </div>
  );
};

export default SessionWarning;
